import { createSlice } from "@reduxjs/toolkit";

export const initialState = {
  queue: [],
  duplicates: [],
  files: [], // { id, file, status, progress, backendData, uploading }
  status: "idle", // GLOBAL queue status
  progress: 0,
  workerRunning: false,
  consumedJobs: {},
  activeJobs: {},
  activeUploads: {},
  hidden: true,
};

const uploadQueueSlice = createSlice({
  name: "uploadQueue",
  initialState,
  reducers: {
    // ----------------------------
    // ADD FILE JOB TO QUEUE
    // ----------------------------
    // addFiles: (state, action) => {
    //   state.queue = [];
    //   state.queue.push({
    //     type: "files",
    //     items: action.payload.files,
    //     meta: action.payload.meta, // includes jobId
    //   });
    // },

    addFiles(state, action) {
      state.hidden = false;

      const { files, meta } = action.payload;
      const jobId = meta.jobId;

      // 🚫 job already processed
      if (state.consumedJobs[jobId]) return;

      state.queue.push({
        jobId,
        type: "files",
        items: files,
        meta,
      });
    },

    appendNewFiles: (state, action) => {
      const existingIds = new Set(state.files.map((f) => f.id));

      const uniqueNewFiles = action.payload.filter(
        (f) => !existingIds.has(f.id),
      );

      state.files.push(...uniqueNewFiles);
    },

    markJobConsumed(state, action) {
      state.consumedJobs[action.payload] = true;
      state.queue = state.queue.filter((job) => job.jobId !== action.payload);
    },

    markJobInFlight(state, action) {
      state.activeJobs[action.payload] = true;
    },

    markJobFinished(state, action) {
      delete state.activeJobs[action.payload];
    },

    // ----------------------------
    // DUPLICATES FROM SERVER
    // ----------------------------
    // setDuplicates: (state, action) => {
    //   state.duplicates = action.payload;
    // },
    setDuplicates: (state, action) => {
      const incoming = action.payload;

      const existingHashes = new Set(state.duplicates.map((d) => d.file_hash));

      const uniqueIncoming = incoming.filter(
        (d) => !existingHashes.has(d.file_hash),
      );

      state.duplicates.push(...uniqueIncoming);
    },

    removeDuplicate: (state, action) => {
      state.duplicates = state.duplicates.filter(
        (d) => d.file_hash !== action.payload,
      );
    },
    removeDuplicatesByHash: (state, action) => {
      const hashes = new Set(action.payload);
      state.duplicates = state.duplicates.filter(
        (d) => !hashes.has(d.file_hash),
      );
    },

    // ----------------------------
    // NEW FILES AFTER DUP CHECK
    // ----------------------------
    setNewFiles: (state, action) => {
      state.files = action.payload;
    },

    // ----------------------------
    // GLOBAL QUEUE STATUS ONLY
    // ----------------------------
    setStatus: (state, action) => {
      state.status = action.payload;
    },

    // ----------------------------
    // UPDATE SINGLE FILE
    // ----------------------------
    updateFile: (state, action) => {
      const { id, status, progress, backendData, uploading } = action.payload;
      const file = state.files.find((f) => f.id === id);
      if (!file) return;

      if (status) file.status = status;
      if (progress !== undefined) file.progress = progress;
      if (backendData) file.backendData = backendData;
      if (uploading !== undefined) file.uploading = uploading;
    },

    // ----------------------------
    // REMOVE FILE AFTER SUCCESS
    // ----------------------------
    removeNewFile: (state, action) => {
      state.files = state.files.filter((f) => f.id !== action.payload);
    },

    markUploadStarted(state, action) {
      state.activeUploads[action.payload] = true;
    },

    markUploadFinished(state, action) {
      delete state.activeUploads[action.payload];
    },

    setWorkerRunning: (state, action) => {
      state.workerRunning = action.payload;
    },

    hideQueue: (state) => {
      state.hidden = true;
    },

    showQueuePanel: (state) => {
      state.hidden = false;
    },

    // ----------------------------
    // CLEAR EVERYTHING
    // ----------------------------
    clearQueue: (state) => {
      state.queue = [];
      state.duplicates = [];
      state.files = [];
      state.status = "idle";
      state.progress = 0;
      state.activeJobs = {};
      state.activeUploads = {};
    },
  },
});

export const {
  addFiles,
  setDuplicates,
  setNewFiles,
  appendNewFiles,
  setStatus,
  updateFile,
  markJobConsumed,
  markJobInFlight,
  markJobFinished,
  removeNewFile,
  removeDuplicate,
  removeDuplicatesByHash,
  clearQueue,
  setWorkerRunning,
  markUploadStarted,
  markUploadFinished,
  hideQueue,
  showQueuePanel,
} = uploadQueueSlice.actions;

export default uploadQueueSlice.reducer;
